export type TranslateFn = (key: string, params?: Record<string, unknown>) => string

export interface CustomerImportFail {
  row_number: number
  errors: string[]
  row?: {
    firstname?: string
    lastname?: string
    email?: string
    phone_number?: string
    program?: string
    [key: string]: string | undefined
  }
}

export interface CustomerImportResult {
  success: unknown[]
  fails: CustomerImportFail[]
}

const ERROR_CODE_KEYS: Record<string, string> = {
  EMAIL_ALREADY_EXISTS: 'customers.import.errors.EMAIL_ALREADY_EXISTS',
  MISSING_EMAIL: 'customers.import.errors.MISSING_EMAIL',
  MISSING_FIRSTNAME: 'customers.import.errors.MISSING_FIRSTNAME',
  MISSING_LASTNAME: 'customers.import.errors.MISSING_LASTNAME',
  INVALID_EMAIL_FORMAT: 'customers.import.errors.INVALID_EMAIL_FORMAT',
  PROGRAM_NOT_EXIST: 'customers.import.errors.PROGRAM_NOT_EXIST',
  UNSUPPORTED_FILE_FORMAT: 'customers.import.errors.UNSUPPORTED_FILE_FORMAT',
  UNHANDLED_PARSING_ERROR: 'customers.import.errors.UNHANDLED_PARSING_ERROR'
}

const ERROR_CODES = Object.keys(ERROR_CODE_KEYS)
const ERROR_CODE_PATTERN = new RegExp(ERROR_CODES.join('|'))
/** File data CODE;line;{...} or CODE;line;{...} */
const FILE_DATA_ERROR_PATTERN = new RegExp(
  `(?:File data\\s+)?(${ERROR_CODES.join('|')});(\\d+);(.*)$`,
  's'
)

export function translateImportErrorCode(code: string, t: TranslateFn): string {
  const key = ERROR_CODE_KEYS[code]
  if (key) return String(t(key))
  return String(t('customers.import.errors.unknown', { code }))
}

/** Formats a business fail error code or a free-form string. */
export function formatImportFailError(error: string, t: TranslateFn): string {
  const trimmed = error.trim()
  if (ERROR_CODE_KEYS[trimmed]) return translateImportErrorCode(trimmed, t)

  const match = trimmed.match(ERROR_CODE_PATTERN)
  if (match?.[0] && ERROR_CODE_KEYS[match[0]]) {
    return translateImportErrorCode(match[0], t)
  }

  return trimmed
}

function parseRubyOrJsonRow(raw: string): CustomerImportFail['row'] | undefined {
  const trimmed = raw.trim()
  if (!trimmed) return undefined

  const fromRuby: Record<string, string> = {}
  const rubyPair = /"([^"]+)"\s*=>\s*"((?:\\.|[^"\\])*)"/g
  let match: RegExpExecArray | null
  while ((match = rubyPair.exec(trimmed)) !== null) {
    fromRuby[match[1]] = match[2].replace(/\\"/g, '"')
  }
  if (Object.keys(fromRuby).length > 0) return fromRuby

  try {
    const asJson = trimmed.replace(/=>/g, ':')
    const parsed = JSON.parse(asJson)
    if (parsed && typeof parsed === 'object') {
      return Object.keys(parsed).reduce<Record<string, string>>((acc, key) => {
        acc[key] = parsed[key] == null ? '' : String(parsed[key])
        return acc
      }, {})
    }
  } catch {
    // ignore
  }

  return undefined
}

/**
 * Parses backend details like:
 * File data MISSING_EMAIL;9;{"firstname"=>"Imane", "lastname"=>"Leroy", "email"=>"", "phone_number"=>"0611111109"}
 */
export function parseFileDataErrorDetail(raw: string): CustomerImportFail | null {
  if (typeof raw !== 'string' || !raw.trim()) return null

  const match = raw.trim().match(FILE_DATA_ERROR_PATTERN)
  if (!match) {
    const codeOnly = raw.trim().match(ERROR_CODE_PATTERN)
    if (codeOnly?.[0]) {
      return {
        row_number: 0,
        errors: [codeOnly[0]]
      }
    }
    return null
  }

  const [, code, line, rowRaw] = match
  return {
    row_number: Number(line),
    errors: [code],
    row: parseRubyOrJsonRow(rowRaw)
  }
}

export function parseImportApiFails(err: any): CustomerImportFail[] {
  const data = err?.response?.data
  if (!Array.isArray(data?.errors)) return []

  const byLine = new Map<number, CustomerImportFail>()

  data.errors.forEach((error: any) => {
    const raw = error?.details || error?.detail || error?.message || ''
    const parsed = parseFileDataErrorDetail(typeof raw === 'string' ? raw : '')
    if (!parsed) return

    const existing = byLine.get(parsed.row_number)
    if (existing) {
      existing.errors.push(...parsed.errors)
      if (!existing.row && parsed.row) existing.row = parsed.row
    } else {
      byLine.set(parsed.row_number, {
        row_number: parsed.row_number,
        errors: [...parsed.errors],
        row: parsed.row
      })
    }
  })

  return Array.from(byLine.values()).sort((a, b) => a.row_number - b.row_number)
}

/**
 * Parses API error payloads (422, 409, …) into a flat message.
 */
export function formatImportApiError(err: any, t: TranslateFn): string {
  const status = err?.response?.status
  const data = err?.response?.data
  const parsedFails = parseImportApiFails(err)

  if (parsedFails.length > 0) {
    return parsedFails
      .map(fail => {
        const messages = fail.errors.map(code => formatImportFailError(code, t)).join(', ')
        if (fail.row_number > 0) {
          return String(t('customers.import.errors.withLine', {
            line: fail.row_number,
            message: messages
          }))
        }
        return messages
      })
      .join('\n')
  }

  if (Array.isArray(data?.errors) && data.errors.length > 0) {
    const messages = data.errors
      .map((error: any) => error?.details || error?.detail || error?.title || error?.message || '')
      .filter((message: string) => typeof message === 'string' && message.trim())
    if (messages.length > 0) return messages.join('\n')
  }

  if (typeof data?.error === 'string') return data.error
  if (typeof data?.message === 'string') return data.message

  if (status === 409) {
    return String(t('customers.import.errors.conflict'))
  }

  if (status) return String(t('customers.import.errors.network', { status }))
  if (typeof err?.message === 'string' && err.message.trim()) return err.message
  return String(t('customers.import.errors.generic'))
}
